import { isDefined } from "@clipboard-health/util-ts";
import { type TimesheetEntry } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/hooks/useTimesheetEntry";
import { formatBreakDisplay } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/utils/formatBreakDisplay";
import { differenceInMinutes, isAfter, isBefore, parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetProvisionalExtraWorkedTimeRequest } from "../api/useGetProvisionalExtraWorkedTimeRequest";
import {
  isClockInAdjustment,
  isClockOutAdjustment,
  isShiftExtension,
} from "../utils/provisionalRequestDetection";

interface UsePendingHoursAdjustmentParams {
  shiftId: string | undefined;
  isMissedPunchRequestEnabledV2: boolean;
  /** The original recorded timesheet (hcp_clock_in/out). Used as the "verified" baseline. */
  formTimesheet: TimesheetEntry | undefined;
  /** The currently displayed clock-in time (may equal the form value if no adjustment). */
  formClockInTime: Date | undefined;
  formClockOutTime: Date | undefined;
  formBreakStartTime: Date | undefined;
  formBreakEndTime: Date | undefined;
  /**
   * Fallback clock-in time used as the "original" baseline when formTimesheet is undefined
   * (e.g. unverified shifts with no IP clock-in). Typically the scheduled shift start ISO string.
   */
  recordedClockInTime?: string;
  /**
   * Fallback clock-out time used as the "original" baseline when formTimesheet is undefined
   * (e.g. unverified shifts with no IP clock-out). Typically the scheduled shift end ISO string.
   */
  recordedClockOutTime?: string;

  /** IANA timezone of the workplace, used to format original break display text. */
  workplaceTimezone: string;

  /**
   * Whether shift extension rollout functionality is on. This determines whether workers can extend their shift past
   * scheduled end.
   */
  isShiftExtendable?: boolean;
}

export interface UsePendingHoursAdjustmentResult {
  /** Date parsed from the provisional extra worked time request's start, or undefined when none exists. */
  adjustedClockInTime: Date | undefined;
  /**
   * Date parsed from the provisional extra worked time request's end, or undefined when no
   * shift-extension end is set (clock-in-only adjustments) or when the request's end matches
   * the recorded clock-out time.
   */
  adjustedClockOutTime: Date | undefined;
  /** The provisional extra worked time request entity, if one exists for this shift. */
  existingRequest:
    | {
        id: string;
        attributes: {
          start: Date;
          end?: Date;
          reason?: string;
          reasons?: { start?: string; end?: string };
        };
      }
    | undefined;
  /**
   * True when at least one of the three directional adjustment checks is true
   * (hasClockInAdjustment || hasClockOutAdjustment || hasBreakAdjustment).
   * Not gated by `isMissedPunchRequestEnabledV2` — callers that need the flag
   * check (e.g. `showFullShiftReason` in SubmitTimesheetPage) apply it themselves.
   */
  hasPendingHoursAdjustment: boolean;
  /**
   * True when the adjusted clock-in (formClockInTime) is earlier than the recorded clock-in
   * (formTimesheet.clockInTime), meaning the worker is claiming extra time at the start.
   */
  hasClockInAdjustment: boolean;
  /**
   * True when the adjusted clock-out (formClockOutTime) is later than the recorded clock-out
   * (formTimesheet.clockOutTime), meaning the worker is claiming extra time at the end.
   */
  hasClockOutAdjustment: boolean;
  /**
   * Pass as `originalClockInTime` on TimesheetTimeEntries.
   * Only set when the feature is on and a clock-in adjustment exists.
   */
  originalClockInTime: Date | undefined;
  /**
   * Pass as `originalClockOutTime` on TimesheetTimeEntries.
   * Only set when the feature is on and the form clock-out is later than the recorded value.
   */
  originalClockOutTime: Date | undefined;
  /**
   * True when the adjusted break duration (formBreakStartTime/formBreakEndTime) is less than the
   * recorded break duration (formTimesheet), meaning the worker is claiming more paid time by
   * reducing break.
   */
  hasBreakAdjustment: boolean;
  /**
   * Pass as `originalBreakDisplay` on TimesheetTimeEntries.
   * Only set when the V2 flag is on and a break adjustment exists.
   * Formatted from the recorded timesheet break times.
   */
  originalBreakDisplay: string | undefined;
  /**
   * Pass as `verifiedTimesheet` on TimesheetTimeEntries.
   * Only set when the feature is on and more hours are being claimed.
   */
  verifiedTimesheetForEntries: TimesheetEntry | undefined;
  /**
   * True when a provisional extra worked time request exists with a `reasons.end` value,
   * indicating the worker extended past the scheduled shift end.
   */
  hasShiftExtension: boolean;
}

function resolveOriginalClockInTime(params: {
  isMissedPunchRequestEnabledV2: boolean;
  formClockInTime: Date | undefined;
  recordedClockInTime: Date | undefined;
}): Date | undefined {
  const { isMissedPunchRequestEnabledV2, formClockInTime, recordedClockInTime } = params;

  if (!isMissedPunchRequestEnabledV2) {
    return undefined;
  }

  if (!isDefined(formClockInTime) || !isDefined(recordedClockInTime)) {
    return undefined;
  }

  return isBefore(formClockInTime, recordedClockInTime) ? recordedClockInTime : undefined;
}

function resolveOriginalClockOutTime(params: {
  isMissedPunchRequestEnabledV2: boolean;
  adjustedClockOutTime: Date | undefined;
  formTimesheet: TimesheetEntry | undefined;
  formClockOutTime: Date | undefined;
  recordedClockOutTime: Date | undefined;
}): Date | undefined {
  const {
    isMissedPunchRequestEnabledV2,
    adjustedClockOutTime,
    formTimesheet,
    formClockOutTime,
    recordedClockOutTime,
  } = params;

  if (!isMissedPunchRequestEnabledV2) {
    return undefined;
  }

  const isLaterThanRecorded =
    isDefined(formTimesheet) &&
    isDefined(formClockOutTime) &&
    isAfter(formClockOutTime, formTimesheet.clockOutTime);

  if (isDefined(adjustedClockOutTime) || isLaterThanRecorded) {
    return formTimesheet?.clockOutTime ?? recordedClockOutTime;
  }

  return undefined;
}

function resolveOriginalBreakDisplay(params: {
  isMissedPunchRequestEnabledV2: boolean;
  hasBreakAdjustment: boolean;
  formTimesheet: TimesheetEntry | undefined;
  workplaceTimezone: string;
}): string | undefined {
  const { isMissedPunchRequestEnabledV2, hasBreakAdjustment, formTimesheet, workplaceTimezone } =
    params;

  if (!isMissedPunchRequestEnabledV2 || !hasBreakAdjustment || !isDefined(formTimesheet)) {
    return undefined;
  }

  return formatBreakDisplay({ displayTimesheet: formTimesheet, workplaceTimezone });
}

/**
 * Consolidates the pending-hours-adjustment data fetching and derived state
 */
export function usePendingHoursAdjustment(
  params: UsePendingHoursAdjustmentParams
): UsePendingHoursAdjustmentResult {
  const {
    shiftId,
    isMissedPunchRequestEnabledV2,
    isShiftExtendable = false,
    formTimesheet,
    formClockInTime,
    formClockOutTime,
    formBreakStartTime,
    formBreakEndTime,
    recordedClockInTime: recordedClockInTimeIso,
    recordedClockOutTime: recordedClockOutTimeIso,
    workplaceTimezone,
  } = params;

  const recordedClockInTime = useMemo(
    () =>
      formTimesheet?.clockInTime ??
      (recordedClockInTimeIso ? parseISO(recordedClockInTimeIso) : undefined),
    [formTimesheet?.clockInTime, recordedClockInTimeIso]
  );

  const recordedClockOutTime = useMemo(
    () =>
      formTimesheet?.clockOutTime ??
      (recordedClockOutTimeIso ? parseISO(recordedClockOutTimeIso) : undefined),
    [formTimesheet?.clockOutTime, recordedClockOutTimeIso]
  );

  const { data: provisionalExtraWorkedTimeData } = useGetProvisionalExtraWorkedTimeRequest(
    shiftId ?? "",
    {
      enabled:
        (isMissedPunchRequestEnabledV2 || isShiftExtendable) &&
        isDefined(shiftId) &&
        shiftId.length > 0,
    }
  );
  const existingRequest = provisionalExtraWorkedTimeData?.data[0];

  const existingRequestStartIso = existingRequest?.attributes.start;
  const existingRequestEndIso = existingRequest?.attributes.end;

  const adjustedClockInTime = useMemo(() => {
    if (!isDefined(existingRequestStartIso)) {
      return undefined;
    }

    const existingRequestStart = new Date(existingRequestStartIso);

    if (
      isDefined(recordedClockInTime) &&
      !isClockInAdjustment(existingRequestStart, recordedClockInTime)
    ) {
      return undefined;
    }

    return existingRequestStart;
  }, [existingRequestStartIso, recordedClockInTime]);

  const adjustedClockOutTime = useMemo(() => {
    if (!isDefined(existingRequestEndIso)) {
      return undefined;
    }

    const existingRequestEnd = new Date(existingRequestEndIso);

    if (!isClockOutAdjustment(existingRequestEnd, recordedClockOutTime)) {
      return undefined;
    }

    return existingRequestEnd;
  }, [existingRequestEndIso, recordedClockOutTime]);

  const hasClockInAdjustment =
    isDefined(formTimesheet) &&
    isDefined(formClockInTime) &&
    isBefore(formClockInTime, formTimesheet.clockInTime);

  const hasClockOutAdjustment =
    isDefined(formTimesheet) &&
    isDefined(formClockOutTime) &&
    isAfter(formClockOutTime, formTimesheet.clockOutTime);

  const hasBreakAdjustment = (() => {
    if (!isDefined(formTimesheet)) {
      return false;
    }

    const recordedBreakMinutes =
      isDefined(formTimesheet.breakStartTime) && isDefined(formTimesheet.breakEndTime)
        ? differenceInMinutes(formTimesheet.breakEndTime, formTimesheet.breakStartTime)
        : 0;
    const adjustedBreakMinutes =
      isDefined(formBreakStartTime) && isDefined(formBreakEndTime)
        ? differenceInMinutes(formBreakEndTime, formBreakStartTime)
        : 0;

    return adjustedBreakMinutes < recordedBreakMinutes;
  })();

  const hasPendingHoursAdjustment =
    hasClockInAdjustment || hasClockOutAdjustment || hasBreakAdjustment;

  const originalClockInTime = resolveOriginalClockInTime({
    isMissedPunchRequestEnabledV2,
    formClockInTime,
    recordedClockInTime,
  });

  const originalClockOutTime = resolveOriginalClockOutTime({
    isMissedPunchRequestEnabledV2,
    adjustedClockOutTime,
    formTimesheet,
    formClockOutTime,
    recordedClockOutTime,
  });

  const originalBreakDisplay = resolveOriginalBreakDisplay({
    isMissedPunchRequestEnabledV2,
    hasBreakAdjustment,
    formTimesheet,
    workplaceTimezone,
  });

  return {
    adjustedClockInTime,
    adjustedClockOutTime,
    existingRequest,
    hasPendingHoursAdjustment,
    hasClockInAdjustment,
    hasClockOutAdjustment,
    hasBreakAdjustment,
    hasShiftExtension: isShiftExtension(existingRequest?.attributes?.reasons),
    originalBreakDisplay,
    originalClockInTime,
    originalClockOutTime,
    verifiedTimesheetForEntries: hasPendingHoursAdjustment ? formTimesheet : undefined,
  };
}
